import { IMBALANCE_TOLERANCE } from '../../live/unhedged';
import type { OpenerDescalceRow } from '../../api/useOpenerDescalce';

/** Client-side filter state applied over the already-fetched snapshot. */
export interface DescalceFilterState {
  search: string;
  minDescalce: number;
  onlyGroupsOn: boolean;
  hideResolving: boolean;
  onlyProd: boolean;
  onlyTest: boolean;
  onlyDivergence: boolean;
}

/**
 * `hideResolving` is OFF by default. `resolving` marks that ONE side is cash
 * now, never that the imbalance is zero — the backend reconstructs a settled
 * Kalshi leg from its settlement net, so a pair that settled flat already
 * arrives at `descalce: 0` and falls under the numeric floor on its own. What
 * a resolving row still shows is a real residual, and hiding that by default
 * is the failure this screen exists to prevent. The toggle stays for an
 * operator who wants a strictly-live view.
 */
export const DEFAULT_DESCALCE_FILTERS: DescalceFilterState = {
  search: '',
  minDescalce: 0.5,
  onlyGroupsOn: false,
  hideResolving: false,
  onlyProd: false,
  onlyTest: false,
  onlyDivergence: false
};

function matchesSearch(row: OpenerDescalceRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = `${row.pairName ?? ''} ${row.group.name ?? ''}`.toLowerCase();
  return haystack.includes(q);
}

/**
 * Two kinds of row are never hidden behind the numeric floor, because for
 * neither is the number a real cross-venue descalce to threshold:
 *  - `venue === null` (unconfigured / starting / error account) — exactly the
 *    "unknown" the screen exists to surface.
 *  - `marketsIncomplete` — the backend emits these with counts it deliberately
 *    refused to attribute. A rule whose two Polymarket tokens collide has both
 *    Poly sides forced to `0` while real size is held, so it arrives at
 *    `descalce: 0` BY CONSTRUCTION and the floor would delete the one row the
 *    backend went out of its way to keep visible.
 */
function matchesMinDescalce(row: OpenerDescalceRow, min: number): boolean {
  if (min <= 0) return true;
  if (!row.venue) return true;
  if (row.venue.marketsIncomplete === true) return true;
  return row.venue.descalce >= min;
}

function matchesDivergence(row: OpenerDescalceRow): boolean {
  if (row.divergence == null) return false;
  return Math.abs(row.divergence.total) > IMBALANCE_TOLERANCE;
}

export function applyDescalceFilters(
  rows: OpenerDescalceRow[],
  filters: DescalceFilterState
): OpenerDescalceRow[] {
  return rows.filter(row => {
    if (!matchesSearch(row, filters.search)) return false;
    if (!matchesMinDescalce(row, filters.minDescalce)) return false;
    if (filters.onlyGroupsOn && row.group.podsDesired !== 'on') return false;
    if (filters.hideResolving && row.venue?.resolving === true) return false;
    if (filters.onlyProd && row.account !== 'prod') return false;
    if (filters.onlyTest && row.account !== 'test') return false;
    if (filters.onlyDivergence && !matchesDivergence(row)) return false;
    return true;
  });
}

/**
 * How many rows `hideResolving` ALONE is suppressing — rows that clear every
 * other active filter and are hidden only because a leg already resolved.
 * `0` when the filter is off. The header strip reports it so a default-on
 * filter never hides rows silently.
 */
export function countResolvingHidden(
  rows: OpenerDescalceRow[],
  filters: DescalceFilterState
): number {
  if (!filters.hideResolving) return 0;
  return applyDescalceFilters(rows, { ...filters, hideResolving: false }).filter(
    row => row.venue?.resolving === true
  ).length;
}

export interface DescalceTotals {
  rowCount: number;
  /** Sum of `venue.descalce` over NON-ambiguous rows only. */
  totalDescalce: number;
  ambiguousCount: number;
  resolvingCount: number;
}

/**
 * Header-strip totals. Ambiguous rows are excluded from the summed total —
 * each ambiguous claimant carries the FULL market size (never an invented
 * split, per the sweeper's own invariant), so summing them in would double
 * (or N-fold) count contracts that live on a single venue position.
 */
export function computeDescalceTotals(
  rows: OpenerDescalceRow[]
): DescalceTotals {
  let totalDescalce = 0;
  let ambiguousCount = 0;
  let resolvingCount = 0;
  for (const row of rows) {
    if (!row.venue) continue;
    if (row.venue.ambiguous) {
      ambiguousCount++;
      continue;
    }
    totalDescalce += row.venue.descalce;
    if (row.venue.resolving) resolvingCount++;
  }
  return { rowCount: rows.length, totalDescalce, ambiguousCount, resolvingCount };
}
